import "./details.scss";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from '../../firebase';
import { doc, getDoc, getDocs, collection } from "firebase/firestore";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { formatFecha } from "./DetailsUtils";
import { useSidebarState } from "../../components/sidebar/SidebarStateContext";
import { uploadErrorLogToFirestore } from "../../utils/logUtils"

/**
 * Componente de la página de detalles de un documento.
 *
 * @returns {JSX.Element} Elemento JSX que representa la página de detalles de un documento.
 */
const Details = () => {
  const { id } = useParams();
  const [documentData, setDocumentData] = useState(null);
  const [isMounted, setIsMounted] = useState(false);

  /**
 * Establece el estado `isMounted` en `true` al montar el componente y lo establece en `false` al desmontarlo.
 */
  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    /**
  * Recupera los datos del documento y los almacena en el estado `documentData`.
  */
    const fetchDocumentData = async () => {
      try {
        const serviciosSnapshot = await getDocs(collection(db, "servicios"));

        for (const servicioDoc of serviciosSnapshot.docs) {
          const documentoRef = doc(servicioDoc.ref, "documents", id);
          const documentSnapshot = await getDoc(documentoRef);

          if (documentSnapshot.exists() && isMounted) {
            setDocumentData(documentSnapshot.data());
            break;
          }
        }
      } catch (err) {
        console.log(err);
        uploadErrorLogToFirestore(err)
      }
    };

    fetchDocumentData();
  }, [id, isMounted]);

  // Función auxiliar para procesar imgKey
  const processImgKey = (imgKey) => {
    const parts = imgKey.split('_');
    if (parts.length === 2 && !isNaN(parts[1])) {
      // Incrementa el número en uno y reconstruye la clave
      return `${parts[0]} ${parseInt(parts[1], 10) + 1}`;
    }
    return imgKey;
  };

  /**
   * Convierte el valor de un campo (posiblemente anidado) en texto legible para mostrar en detalles.
   * Soporta descriptores de formulario con .value, .nestedFields, arrays, objetos y primitivos.
   */
  const formatFieldValueForDisplay = (v) => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) {
      const parts = v.map((item) => {
        if (item != null && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'value')) return formatFieldValueForDisplay(item.value);
        return formatFieldValueForDisplay(item);
      });
      return parts.filter(Boolean).join(', ');
    }
    if (typeof v === 'object') {
      if (Object.prototype.hasOwnProperty.call(v, 'value')) return formatFieldValueForDisplay(v.value);
      // Sección repetible: contenido real en nestedFields
      if (Array.isArray(v.nestedFields) && v.nestedFields.length > 0) {
        return v.nestedFields.map((item, i) => formatFieldValueForDisplay(item)).filter(Boolean).join(' · ');
      }
      const omitKeys = ['name', 'type', 'index', 'nestedFields', 'instanceCount'];
      const entries = Object.entries(v).filter(([k]) => !omitKeys.includes(k));
      if (entries.length === 0) return '';
      if (entries.length === 1 && (entries[0][1] == null || typeof entries[0][1] !== 'object')) return String(entries[0][1]);
      return entries.map(([k, val]) => `${k}: ${formatFieldValueForDisplay(val)}`).join(' · ');
    }
    return String(v);
  };

  /**
 * Checks if a value is a base64 signature
 * @param {string} value - The value to check
 * @returns {boolean} - True if the value is a signature in base64 format
 */
  const isSignature = (value) => {
    return typeof value === 'string' &&
      (value.startsWith('data:image/png;base64,') ||
        value.startsWith('data:image/jpeg;base64,') ||
        // Also check for raw base64 content that might be missing the prefix
        (value.length > 100 && /^[A-Za-z0-9+/=]+$/.test(value)));
  };

  /**
   * Ensures a base64 signature has the proper prefix.
   * Defensive: document data can be non-string (object, number, Firestore types); avoids value.startsWith is not a function.
   * @param {*} value - The signature data (string, or corrupt/unexpected type)
   * @returns {string} - Normalized signature string, or '' if value is not usable
   */
  const normalizeSignature = (value) => {
    if (value == null) return '';
    if (typeof value !== 'string') return '';

    // If already in correct format, return as is
    if (value.startsWith('data:image/')) {
      return value;
    }

    // If it's just the base64 string without prefix
    if (!value.includes(';base64,') && /^[A-Za-z0-9+/=]+$/.test(value)) {
      return `data:image/png;base64,${value}`;
    }

    return value;
  };


  /**
 * Renderiza los campos del documento.
 *
 * @returns {JSX.Element|null} Elemento JSX que representa los campos del documento o `null` si no hay datos.
 */
  const renderFields = () => {
    if (!documentData) {
      return null;
    }

    return Object.entries(documentData.fields).map(([key, value]) => {
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();

      if (value === null) {
        return (
          <div className="detailItem" key={key}>
            <span className="itemKey">{key}:</span>
            <span className="itemValue">Campo vacío</span>
          </div>
        );
      }
      // New condition: Check if this is a signature field
      if (isSignature(value) || key.toLowerCase().includes('firma')) {
        const normalizedSignature = normalizeSignature(value);
        return (
          <div className="detailItem" key={key}>
            <span className="itemKey">{formattedKey}:</span>
            <div className="imageContainer">
              {normalizedSignature ? (
                <img src={normalizedSignature} alt={`${formattedKey}`} className="itemValue formImg" />
              ) : (
                <span className="itemValue">Sin imagen o datos no válidos</span>
              )}
            </div>
          </div>
        );
      }
      if (typeof value === "string" && value.startsWith("https://")) {
        return (
          <div className="detailItem" key={key}>
            <span className="itemKey">{formattedKey}:</span>
            <div className="imageContainer">
              <a href={value} className="imageLink" target="_blank" rel="noopener noreferrer">
                <img src={value} alt={key} className="itemValue formImg" />
              </a>
            </div>
          </div>
        );

      } else if (typeof value === "object") {
        // Objeto tipo descriptor de formulario: { value, name, type, index, nestedFields, ... } → mostrar solo el valor real
        const fieldValue = value.value;
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
          const display = formatFieldValueForDisplay(fieldValue);
          return (
            <div className="detailItem" key={key}>
              <span className="itemKey">{formattedKey}:</span>
              <span className="itemValue">{display}</span>
            </div>
          );
        }
        // Mapa de fotos: { foto_0: url, foto_1: url, ... } → cada entrada es una imagen
        const allValuesAreImageUrls = Object.values(value).every(
          (v) => typeof v === 'string' && (v.startsWith('http') || v.startsWith('data:image/'))
        );
        if (allValuesAreImageUrls) {
          return Object.entries(value).map(([imgKey, imgValue]) => {
            const newKey = processImgKey(imgKey);
            return (
              <div className="detailItem" key={`${key}-${imgKey}`}>
                <span className="itemKey">{`${formattedKey}: ${newKey}.`}</span>
                <div className="imageContainer">
                  <a href={imgValue} className="imageLink" target="_blank" rel="noopener noreferrer">
                    <img src={imgValue} alt={`${key} (${newKey})`} className="itemValue formImg" />
                  </a>
                </div>
              </div>
            );
          });
        }
        // Otro objeto: mostrar representación legible
        const displayValue = formatFieldValueForDisplay(value);
        return (
          <div className="detailItem" key={key}>
            <span className="itemKey">{formattedKey}:</span>
            <span className="itemValue">{displayValue}</span>
          </div>
        );
      } else {
        if (key === "observaciones" && typeof value === 'string') {
          const observacionesLines = value.split('\n').map((line, index) => (
            <div key={`${key}-${index}`} className="itemValue">{line}</div>
          ));
          return (
            <div className="detailItem" key={key}>
              <span className="itemKey">{formattedKey}:</span>
              <div className="observaciones">{observacionesLines}</div>
            </div>
          );
        } else {
          const displayValue = value != null && typeof value === 'object' ? JSON.stringify(value) : value;
          return (
            <div className="detailItem" key={key}>
              <span className="itemKey">{formattedKey}:</span>
              <span className="itemValue">{displayValue}</span>
            </div>
          );
        }
      }
    });
  };

  const { collapsed } = useSidebarState();

  // Agregar una clase basada en el estado de colapso
  const containerClass = collapsed ? "detailContainer collapsed" : "detailContainer";

  return (
    <div className="detail">
      <Sidebar />
      <div className={containerClass}>
        <Navbar />
        <div className="details">
          <h1 className="title">{documentData?.documentTypeId}</h1>
          <div className="top">
            <div className="left">
              <div className="detailItem">
                <div className="top-info">
                  <span className="vigilante">Usuario: &nbsp;</span>
                  <span className="vigilante">{typeof documentData?.vigilante === 'string' ? documentData.vigilante.replace('@gmail.com', '') : (documentData?.vigilante ?? '')}</span>
                  <span className="fecha">{formatFecha(documentData?.fecha)}</span>
                </div>
              </div>
              <div className="separator"></div>
              <div className="itemsContainer">{renderFields()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;
